import { ParsedUrlQuery } from 'querystring'
import { UrlObject } from 'url'

import { I18nParams } from '@/utils/i18n/i18n-params-context'
import { Locale } from '@/utils/i18n/types'
import { Market, MarketEntry, Markets } from '@/utils/multi-markets/types'
import routes from '@/i18n-routes.config'
import { first } from '@/utils/common'

export function translatePathnameLinks(
  pathname: string,
  locale: Locale,
  defaultLocale: Locale
): string {
  const path = routes[pathname]?.[locale] ?? pathname
  const prefix = (defaultLocale ?? 'en') === locale ? '' : `/${locale}`

  return prefix + path
}

export function translatePathname(
  asPath: string,
  pathname: string,
  locale: Locale
): string {
  const locales = ['pl', 'en', 'de', 'ae', 'ar']
  const asPathWithoutLocale =
    asPath.replace(new RegExp(`^/(${locales.join('|')})(?=/|$)`), '') || '/'

  const routeTranslations = routes[pathname]
  if (!routeTranslations) {
    return asPathWithoutLocale
  }
  const localizedRoute = routeTranslations[locale]
  if (!localizedRoute) {
    return asPathWithoutLocale
  }

  const splittedPathname = localizedRoute.split('/').slice(1)
  const splittedAsPath = asPathWithoutLocale.split('/').slice(1)

  const translatedPathArray = splittedPathname?.map((el, i) => {
    if (el.includes('[')) {
      const segment = splittedAsPath[i]
      const segmentKey = '/' + segment
      if (routes[segmentKey] && routes[segmentKey][locale]) {
        const translatedSegment = routes[segmentKey][locale]
        return translatedSegment
      } else {
        return segment
      }
    } else return el
  })

  const translatedPath = '/' + translatedPathArray.join('/')

  return translatedPath.replace(/\/+/g, '/')
}

export function toAbsoluteURL(
  asPath: string,
  pathname: string,
  locale: Locale,
  market: MarketEntry
): string {
  const defaultLocale = market.defaultLocale ?? 'en'
  let absoluteUrl =
    'https://' + market.host + translatePathname(asPath, pathname, locale)

  if (locale !== defaultLocale) {
    absoluteUrl =
      'https://' +
      market.host +
      `/${locale}` +
      translatePathname(asPath, pathname, locale)
  }

  return absoluteUrl
}

export function toAbsoluteURLLinks(
  pathname: string,
  locale: Locale,
  market: MarketEntry
): string {
  return (
    'https://' +
    market.host +
    translatePathnameLinks(pathname, locale, market.defaultLocale ?? 'en')
  )
}

export function i18nParamsForLocale(
  query: ParsedUrlQuery,
  i18nParams: I18nParams,
  locale: Locale
): ParsedUrlQuery {
  const params = { ...query }

  if (i18nParams) {
    for (const param in i18nParams) {
      params[param] = i18nParams[param][locale]
    }
  }
  for (const path in params) {
    const value = `/${params[path] as string}`

    if (routes[value] && routes[value][locale]) {
      params[path] = routes[value][locale]?.slice(1)
    }
  }

  return params
}

export function dynamicRouteFromQuery(
  pathname: string,
  query?: UrlObject['query']
): { pathname: string; query?: UrlObject['query'] } {
  if (typeof query !== 'object' || query === null) return { pathname, query }

  let parsedPathname = pathname
  const dynamicParams: string[] = []

  for (const param in query) {
    parsedPathname = parsedPathname.replace(
      new RegExp(`\\[${param}\\]`, 'g'),
      () => {
        dynamicParams.push(param)
        return String(first(query[param] as string) ?? '')
      }
    )
  }

  return {
    pathname: parsedPathname,
    query: Object.fromEntries(
      Object.entries(query).filter(([param]) => !dynamicParams.includes(param))
    )
  }
}

export const fallbackMarket: Market = 'pl'

export function getMarketByHost(host: string, markets: Markets): Market {
  const market = Object.entries(markets).find(
    ([, value]) => value.host === host
  )

  return market ? (market[0] as Market) : fallbackMarket
}
